// MA-891 — the badge earn rules (KID-JOURNEY-2026-10-08 D-KJ-3, D-KJ-4, D-KJ-5, D-KJ-19).
//
// Built on the REAL producers only, never a re-derivation:
//   - the tree node: skillGraphNodesFromAttempts (useSkillGraph.ts, the hook's own
//     pure node builder) and masteryTier (masteryTiers.ts) over node.mastery.mastery_score;
//   - first tries: isFirstAttempt (canonicalEvidence.ts);
//   - the Foundation audience: audienceForSkill (skillMeta.ts);
//   - the local day: localDayKey (celebrationLatch.ts, device timezone).
// Pure: no React, no storage, no clock (callers pass the attempt rows / event times).
//
// MA-919 (operator decision D-1010-5): for a skill whose served pool, for the audience it
// is served with, holds no launch_safe item (derived on every call from the real reader,
// pullItems), the tree score can never move (canCountTowardMastery counts launch_safe
// content only), so silver and the D-KJ-3 Foundation gold path read the engine's own skill
// band (aggregateSkillStatesFromAttempts → masteryBand secure or fluent) in place of the
// tree tier. Every other skill reads the tree tier as before. Spec:
// docs/build/specs/MA-919-engine-band-badges.md.

import type { ChildAttempt } from "@/adaptation/types";
import { skillGraphNodesFromAttempts } from "../useSkillGraph";
import { masteryTier } from "../masteryTiers";

type MasteryTier = ReturnType<typeof masteryTier>;
import { isFirstAttempt } from "../canonicalEvidence";
import { audienceForSkill } from "../skillMeta";
import { pullItems } from "../useEngineQuestions";
import { aggregateSkillStatesFromAttempts } from "../progression";
import { localDayKey } from "@/engine/celebrationLatch";
import { PRACTICE_DAY_THRESHOLDS, REPEATING_DAY_THRESHOLDS, daysBadgeId, daysInstanceId } from "./ids";

/** The engine's session-key convention (sessionId ?? runId ?? per-timestamp). A copy of
 *  progression.ts's private `sessionKey` (progression.ts:357-359); exporting that helper is an
 *  engine-file change outside this slice (RED round 1 NIT-1). */
function sessionKeyOf(a: ChildAttempt): string {
  return a.sessionId ?? a.runId ?? `t:${a.timestamp}`;
}

/** Local calendar day of an ISO timestamp. */
export function dayOf(iso: string): string {
  return localDayKey(new Date(iso));
}

const AT_LEAST_SECURE: ReadonlySet<MasteryTier> = new Set<MasteryTier>(["secure", "mastered"]);

/** The tree tier of one skill over the given attempt rows (the tree's projection:
 *  useSkillGraph's node builder, then masteryTier of mastery_score). */
export function treeTierOf(skillId: string, attempts: readonly ChildAttempt[]): MasteryTier {
  const rows = attempts.filter((a) => a.skillId === skillId);
  if (rows.length === 0) return masteryTier(null);
  const node = skillGraphNodesFromAttempts(rows, "badge").find((n) => n.id === skillId);
  return masteryTier(node?.mastery?.mastery_score);
}

/** A served item as far as the launch-safety read needs it. */
export interface ServedPoolItem { launchStatus?: string | null }
/** Options for the earn rules. `servedPool` replaces the real reader (tests only).
 *  `engineBandSkill` is the per-build memo of servedPoolHoldsNoLaunchSafe (engineBandSkillMemo):
 *  buildBadgeBook makes one per build so each skill's pool is read at most once per build
 *  (RED round 1 SHOULD-3); a new build reads the pool again, so the rule still self-heals. */
export interface EarnRuleOptions {
  servedPool?: (skillId: string) => readonly ServedPoolItem[];
  engineBandSkill?: (skillId: string) => boolean;
}

/** The real served pool: every item of the skill's mapped unique pool for the audience it
 *  is served with (audienceForSkill; FoundationSession passes the same "scaffolded"). The
 *  itemFilter runs over the FULL unique pool before truncation, so it sees every item;
 *  count 1 never enters a repetition round (no content-pool breadcrumb). */
function realServedPool(skillId: string): ServedPoolItem[] {
  const seen: ServedPoolItem[] = [];
  pullItems(skillId, 1, audienceForSkill(skillId), null, undefined, (item) => {
    seen.push({ launchStatus: item.launchStatus });
    return true;
  });
  return seen;
}

/** MA-919: true when the skill's served pool is non-empty and holds no launch_safe item
 *  (an absent launchStatus reads launch_safe, as the screens stamp it). Derived on every
 *  call, never from a list. An empty pool is false: the skill is not served, and an
 *  independent_practice pool is launch_safe-only, so this can only hold for a
 *  scaffolded (Foundation) skill. */
export function servedPoolHoldsNoLaunchSafe(skillId: string, opts: EarnRuleOptions = {}): boolean {
  const pool = (opts.servedPool ?? realServedPool)(skillId);
  return pool.length > 0 && !pool.some((it) => (it.launchStatus ?? "launch_safe") === "launch_safe");
}

/** MA-919 (RED round 1 SHOULD-3): servedPoolHoldsNoLaunchSafe memoised per skill for ONE
 *  build. Never module-level: each call returns a fresh memo. */
export function engineBandSkillMemo(opts: EarnRuleOptions = {}): (skillId: string) => boolean {
  const memo = new Map<string, boolean>();
  return (skillId) => {
    let v = memo.get(skillId);
    if (v === undefined) {
      v = servedPoolHoldsNoLaunchSafe(skillId, opts);
      memo.set(skillId, v);
    }
    return v;
  };
}

/** True when this skill takes the engine-band path (the memo when given, else a fresh read). */
function isEngineBandSkill(skillId: string, opts: EarnRuleOptions): boolean {
  return opts.engineBandSkill ? opts.engineBandSkill(skillId) : servedPoolHoldsNoLaunchSafe(skillId, opts);
}

/** MA-919: the engine's own skill band over the given rows (the real aggregator,
 *  progression.ts aggregateSkillStatesFromAttempts; "new" with no rows). */
export function engineBandOf(skillId: string, attempts: readonly ChildAttempt[]): string {
  const rows = attempts.filter((a) => a.skillId === skillId);
  return aggregateSkillStatesFromAttempts(rows)[skillId]?.masteryBand ?? "new";
}

const ENGINE_AT_LEAST_SECURE: ReadonlySet<string> = new Set(["secure", "fluent"]);

/** "At least secure" for a skill: the tree tier (secure or mastered), or, for a skill whose
 *  served pool holds no launch_safe item, the engine band (secure or fluent). */
function atLeastSecureReader(skillId: string, engine: boolean): (rows: readonly ChildAttempt[]) => boolean {
  if (engine) return (rows) => ENGINE_AT_LEAST_SECURE.has(engineBandOf(skillId, rows));
  return (rows) => AT_LEAST_SECURE.has(treeTierOf(skillId, rows));
}

/** Chronological sessions (by the time of their last attempt) that hold an attempt on
 *  `skillId`, each with the index (into the sorted skill rows) of its last attempt. */
function skillSessions(rows: readonly ChildAttempt[]): Array<{ key: string; lastIdx: number }> {
  const last = new Map<string, number>();
  rows.forEach((a, i) => last.set(sessionKeyOf(a), i));
  return [...last.entries()].map(([key, lastIdx]) => ({ key, lastIdx })).sort((x, y) => x.lastIdx - y.lastIdx);
}

function sortedSkillRows(skillId: string, attempts: readonly ChildAttempt[]): ChildAttempt[] {
  return attempts
    .filter((a) => a.skillId === skillId)
    .slice()
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

/** Silver (D-KJ-19): the tree tier, computed over FIRST-TRY attempts only, is at
 *  least "secure" at the end of at least two distinct sessions that hold an attempt
 *  on the skill. MA-919: for a skill whose served pool holds no launch_safe item, the
 *  engine band (secure or fluent) over the same first tries at the end of ONE session
 *  (operator ruling D-1010-12: deriveMasteryBand is "new" below two sessions,
 *  progression.ts:100, so the band holding at a session end already spans two distinct
 *  sessions; requiring two holding ends would ask for three sessions). */
export function silverEarned(skillId: string, attempts: readonly ChildAttempt[], opts: EarnRuleOptions = {}): boolean {
  const rows = sortedSkillRows(skillId, attempts);
  if (rows.length === 0) return false;
  const engine = isEngineBandSkill(skillId, opts);
  const atLeastSecure = atLeastSecureReader(skillId, engine);
  const needed = engine ? 1 : 2;
  let holding = 0;
  for (const s of skillSessions(rows)) {
    const upTo = rows.slice(0, s.lastIdx + 1);
    const firstTries = upTo.filter(isFirstAttempt);
    if (atLeastSecure(firstTries)) holding += 1;
    if (holding >= needed) return true;
  }
  return false;
}

/** True when the skill is served through Foundation only (audience not
 *  independent_practice): the D-KJ-3 carve-out applies to its gold. */
export function isFoundationOnlySkill(skillId: string): boolean {
  return audienceForSkill(skillId) !== "independent_practice";
}

/** Gold (D-KJ-3). Independent skills: the tree reads Mastered. Foundation-only
 *  skills, instead: the tree tier at least secure at the end of a session on local
 *  day D, a correct attempt on a local day after D, and the tier at least secure at
 *  the end of the rows. MA-919: for a skill whose served pool holds no launch_safe item,
 *  "at least secure" is the engine band (secure or fluent) instead of the tree tier. */
export function goldEarned(skillId: string, attempts: readonly ChildAttempt[], opts: EarnRuleOptions = {}): boolean {
  const rows = sortedSkillRows(skillId, attempts);
  if (rows.length === 0) return false;
  if (!isFoundationOnlySkill(skillId)) return treeTierOf(skillId, rows) === "mastered";
  const atLeastSecure = atLeastSecureReader(skillId, isEngineBandSkill(skillId, opts));
  if (!atLeastSecure(rows)) return false;
  for (const s of skillSessions(rows)) {
    const upTo = rows.slice(0, s.lastIdx + 1);
    if (!atLeastSecure(upTo)) continue;
    const d = dayOf(rows[s.lastIdx].timestamp);
    const later = rows.some((a) => a.correct && dayOf(a.timestamp) > d);
    if (later) return true;
  }
  return false;
}

export type SkillStage = "none" | "started" | "silver" | "gold";

/** The stage of a skill badge from the rows alone (no latch): gold, silver, started
 *  (any attempt), none. Gold implies the gold dot even if silver's two-session bar
 *  was never met (the gold rule is the stronger claim). */
export function skillStage(skillId: string, attempts: readonly ChildAttempt[], opts: EarnRuleOptions = {}): SkillStage {
  if (goldEarned(skillId, attempts, opts)) return "gold";
  if (silverEarned(skillId, attempts, opts)) return "silver";
  return attempts.some((a) => a.skillId === skillId) ? "started" : "none";
}

/** Distinct local days with at least one session_completed event (D-KJ-5):
 *  cumulative, never reset; two sessions on one day count once. */
export function practiceDayCount(sessionCompletedAt: readonly string[]): number {
  return new Set(sessionCompletedAt.map(dayOf)).size;
}

/** Every practice-day badge instance held at `count` practice days: 1, 3, 30, 50,
 *  100 once; the 7-day badge at every multiple of 7 (days:7#n) and the 14-day badge
 *  at every multiple of 14 (days:14#n). */
export function dayBadgeInstances(count: number): string[] {
  const out: string[] = [];
  for (const n of PRACTICE_DAY_THRESHOLDS) {
    if (REPEATING_DAY_THRESHOLDS.has(n)) {
      for (let k = 1; k <= Math.floor(count / n); k++) out.push(daysInstanceId(n, k));
    } else if (count >= n) {
      out.push(daysBadgeId(n));
    }
  }
  return out;
}

/** How many times the badge `days:<n>` is held at `count` practice days. */
export function dayBadgeTimesHeld(n: number, count: number): number {
  if (REPEATING_DAY_THRESHOLDS.has(n)) return Math.floor(count / n);
  return count >= n ? 1 : 0;
}

/** A chapter trophy (D-KJ-4, D-KJ-19): every catalogue skill badge of the chapter is
 *  gold, and the chapter has at least one. */
export function trophyEarned(chapterSkillIds: readonly string[], isGold: (skillId: string) => boolean): boolean {
  return chapterSkillIds.length > 0 && chapterSkillIds.every(isGold);
}
